import { IncentiveOfferSellerParticipationLimits } from 'modules/incentivesMarket/types';
import { IncentivesMarketPlanContext } from 'modules/incentivesMarket/types/IncentivesMarketBeneficiaryContext';

import { distributeSellUnitsByPlan } from 'modules/incentivesMarket/pages/IncentiveOfferDetail/utils/distributeSellUnitsByPlan';

type SellerLimitsInput = {
  sellerParticipationLimits?: IncentiveOfferSellerParticipationLimits;
  plans?: IncentivesMarketPlanContext[];
  totalAvailableUnits: number;
  unitPrice?: number;
  planType?: string;
  retentionPercent?: number;
};

const normalizeLimits = (
  limits?: IncentiveOfferSellerParticipationLimits
): Required<IncentiveOfferSellerParticipationLimits> => ({
  allowSellAll: limits?.allowSellAll !== false,
  maxUnits: limits?.maxUnits || 0,
  maxPercentUnits: limits?.maxPercentUnits || 0,
  maxPhantoms: limits?.maxPhantoms || 0,
  maxPercentPhantoms: limits?.maxPercentPhantoms || 0,
  maxStocks: limits?.maxStocks || 0,
  maxPercentStocks: limits?.maxPercentStocks || 0,
  maxWarrants: limits?.maxWarrants || 0,
  maxPercentWarrants: limits?.maxPercentWarrants || 0,
  maxAmount: limits?.maxAmount || 0,
  maxPercentUnvested:
    limits?.maxPercentUnvested === undefined ||
    limits?.maxPercentUnvested === null
      ? 100
      : limits.maxPercentUnvested,
});

const applyInstrumentCap = (
  baseUnits: number,
  maxNumber: number,
  maxPercent: number
): number => {
  const maxAllowed = maxNumber > 0 ? Math.min(baseUnits, maxNumber) : baseUnits;
  const percentAllowed =
    maxPercent > 0 ? Math.floor((baseUnits * maxPercent) / 100) : baseUnits;

  if (maxNumber > 0 && maxPercent > 0) {
    return Math.max(maxAllowed, percentAllowed);
  }
  if (maxNumber > 0) return maxAllowed;
  if (maxPercent > 0) return percentAllowed;
  return baseUnits;
};

const hasAnyTypeCap = (
  limits: Required<IncentiveOfferSellerParticipationLimits>
): boolean =>
  limits.maxPhantoms > 0 ||
  limits.maxPercentPhantoms > 0 ||
  limits.maxStocks > 0 ||
  limits.maxPercentStocks > 0 ||
  limits.maxWarrants > 0 ||
  limits.maxPercentWarrants > 0;

const hasExplicitCapForTypeNormalized = (
  planType: string | undefined,
  limits: Required<IncentiveOfferSellerParticipationLimits>
): boolean => {
  if (planType === 'STOCK_OPTION') {
    return limits.maxStocks > 0 || limits.maxPercentStocks > 0;
  }
  if (planType === 'PHANTOM_SHARE') {
    return limits.maxPhantoms > 0 || limits.maxPercentPhantoms > 0;
  }
  if (planType === 'WARRANT') {
    return limits.maxWarrants > 0 || limits.maxPercentWarrants > 0;
  }
  return false;
};

/**
 * Unified per-type eligibility (mirrors the backend): with allowSellAll, or when no
 * per-type cap is set, every type is eligible; otherwise a type is eligible only if
 * it has an explicit cap (others are excluded).
 */
const isPlanTypeEligible = (
  planType: string | undefined,
  limits: Required<IncentiveOfferSellerParticipationLimits>
): boolean => {
  if (limits.allowSellAll) return true;
  if (!hasAnyTypeCap(limits)) return true;
  return hasExplicitCapForTypeNormalized(planType, limits);
};

const getPlanCappedUnits = ({
  plan,
  limits,
  planType,
}: {
  plan: IncentivesMarketPlanContext;
  limits: Required<IncentiveOfferSellerParticipationLimits>;
  planType?: string;
}): number => {
  const availableUnits = Math.max(0, plan.availableUnits ?? 0);
  const effectiveType = planType ?? plan.planType;

  if (!isPlanTypeEligible(effectiveType, limits)) {
    return 0;
  }

  let cappedUnits = Math.floor((availableUnits * limits.maxPercentUnvested) / 100);

  if (effectiveType === 'STOCK_OPTION') {
    cappedUnits = applyInstrumentCap(
      cappedUnits,
      limits.maxStocks,
      limits.maxPercentStocks
    );
  } else if (effectiveType === 'PHANTOM_SHARE') {
    cappedUnits = applyInstrumentCap(
      cappedUnits,
      limits.maxPhantoms,
      limits.maxPercentPhantoms
    );
  } else if (effectiveType === 'WARRANT') {
    cappedUnits = applyInstrumentCap(
      cappedUnits,
      limits.maxWarrants,
      limits.maxPercentWarrants
    );
  }

  return Math.max(0, cappedUnits);
};

/**
 * SELL semantics: a plan type with NO explicit cap (number and percent both 0)
 * means "exclude this type" (sell nothing), unlike BUY where a missing cap means
 * "no restriction". Used to decide which plans contribute to a SELL offer total.
 */
export const hasExplicitCapForType = (
  planType: string | undefined,
  sellerParticipationLimits?: IncentiveOfferSellerParticipationLimits
): boolean =>
  hasExplicitCapForTypeNormalized(
    planType,
    normalizeLimits(sellerParticipationLimits)
  );

/**
 * SELL offer total: sums the per-plan capped units. Type exclusion is handled inside
 * getPlanCappedUnits (excluded types return 0). `allowSellAll` returns the full
 * available pool across all plans.
 */
export const calculateSellTotalUnitsByTypeLimits = ({
  plans = [],
  sellerParticipationLimits,
}: {
  plans?: IncentivesMarketPlanContext[];
  sellerParticipationLimits?: IncentiveOfferSellerParticipationLimits;
}): number => {
  const limits = normalizeLimits(sellerParticipationLimits);

  if (limits.allowSellAll) {
    return plans.reduce(
      (sum, plan) => sum + Math.max(0, plan.availableUnits ?? 0),
      0
    );
  }

  return plans.reduce(
    (sum, plan) =>
      sum + getPlanCappedUnits({ plan, limits, planType: plan.planType }),
    0
  );
};

export const getSellerPlanMaxSellableUnits = ({
  plan,
  sellerParticipationLimits,
  planType,
}: {
  plan: IncentivesMarketPlanContext;
  sellerParticipationLimits?: IncentiveOfferSellerParticipationLimits;
  planType?: string;
}): number => {
  const limits = normalizeLimits(sellerParticipationLimits);
  const availableUnits = Math.max(0, plan.availableUnits ?? 0);

  if (limits.allowSellAll) {
    return availableUnits;
  }

  return getPlanCappedUnits({ plan, limits, planType });
};

const getSellProfitTotal = ({
  plans,
  totalUnits,
  unitPrice,
  retentionPercent = 0,
}: {
  plans: IncentivesMarketPlanContext[];
  totalUnits: number;
  unitPrice: number;
  retentionPercent?: number;
}): number =>
  distributeSellUnitsByPlan({
    plans,
    totalUnits,
    sellingPrice: unitPrice,
    retentionPercent,
  }).total;

export const isSellProfitWithinBudget = (
  total: number,
  maxTotalSellAmount: number
): boolean => {
  if (maxTotalSellAmount >= 0) {
    return total <= maxTotalSellAmount;
  }

  return total >= maxTotalSellAmount;
};

export const calculateMaxTotalSellAmount = ({
  sellerParticipationLimits,
  plans = [],
  unitPrice = 0,
  planType,
  retentionPercent = 0,
}: Omit<SellerLimitsInput, 'totalAvailableUnits'>): number => {
  const limits = normalizeLimits(sellerParticipationLimits);

  if (limits.allowSellAll) {
    const totalAvailable = plans.reduce(
      (sum, plan) => sum + Math.max(0, plan.availableUnits ?? 0),
      0
    );

    if (!plans.length || totalAvailable <= 0 || unitPrice <= 0) {
      return 0;
    }

    return getSellProfitTotal({
      plans,
      totalUnits: totalAvailable,
      unitPrice,
      retentionPercent,
    });
  }

  if (!plans.length || unitPrice <= 0) {
    return 0;
  }

  let maxTotalSellAmount = 0;

  plans.forEach((plan) => {
    const cappedUnits = getPlanCappedUnits({ plan, limits, planType });

    if (cappedUnits <= 0) {
      return;
    }

    maxTotalSellAmount += getSellProfitTotal({
      plans: [{ ...plan, availableUnits: cappedUnits }],
      totalUnits: cappedUnits,
      unitPrice,
      retentionPercent,
    });
  });

  if (limits.maxAmount > 0) {
    maxTotalSellAmount = Math.min(maxTotalSellAmount, limits.maxAmount);
  }

  return maxTotalSellAmount;
};

export const calculateMaxUnitsForSellBudget = ({
  plans = [],
  maxTotalSellAmount,
  unitPrice = 0,
  retentionPercent = 0,
}: {
  plans?: IncentivesMarketPlanContext[];
  maxTotalSellAmount: number;
  unitPrice?: number;
  retentionPercent?: number;
}): number => {
  const totalAvailable = plans.reduce(
    (sum, plan) => sum + Math.max(0, plan.availableUnits ?? 0),
    0
  );

  if (!plans.length || totalAvailable <= 0 || unitPrice <= 0) {
    return 0;
  }

  let low = 0;
  let high = totalAvailable;
  let best = 0;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const total = getSellProfitTotal({
      plans,
      totalUnits: mid,
      unitPrice,
      retentionPercent,
    });

    if (isSellProfitWithinBudget(total, maxTotalSellAmount)) {
      best = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return best;
};

const calculateMaxUnitsSellerCanOfferLegacy = ({
  limits,
  available,
  unitPrice,
  planType,
}: {
  limits: Required<IncentiveOfferSellerParticipationLimits>;
  available: number;
  unitPrice: number;
  planType?: string;
}): number => {
  if (!isPlanTypeEligible(planType, limits)) {
    return 0;
  }

  let currentUnits = Math.floor((available * limits.maxPercentUnvested) / 100);

  if (planType === 'STOCK_OPTION') {
    currentUnits = applyInstrumentCap(
      currentUnits,
      limits.maxStocks,
      limits.maxPercentStocks
    );
  } else if (planType === 'PHANTOM_SHARE') {
    currentUnits = applyInstrumentCap(
      currentUnits,
      limits.maxPhantoms,
      limits.maxPercentPhantoms
    );
  } else if (planType === 'WARRANT') {
    currentUnits = applyInstrumentCap(
      currentUnits,
      limits.maxWarrants,
      limits.maxPercentWarrants
    );
  }

  if (limits.maxUnits > 0) {
    currentUnits = Math.min(currentUnits, limits.maxUnits);
  }

  if (limits.maxPercentUnits > 0) {
    currentUnits = Math.min(
      currentUnits,
      Math.floor((available * limits.maxPercentUnits) / 100)
    );
  }

  if (limits.maxAmount > 0 && unitPrice > 0) {
    currentUnits = Math.min(
      currentUnits,
      Math.floor(limits.maxAmount / unitPrice)
    );
  }

  return Math.max(0, currentUnits);
};

export const calculateMaxUnitsSellerCanOfferFromPlans = ({
  sellerParticipationLimits,
  plans = [],
  totalAvailableUnits,
  unitPrice = 0,
  planType,
  offerRemainingUnits,
  retentionPercent = 0,
}: SellerLimitsInput & { offerRemainingUnits?: number }): number => {
  const limits = normalizeLimits(sellerParticipationLimits);
  const available = Math.max(0, totalAvailableUnits || 0);
  const remaining =
    offerRemainingUnits === undefined
      ? Number.POSITIVE_INFINITY
      : Math.max(0, offerRemainingUnits || 0);

  if (limits.allowSellAll) {
    return Math.min(available, remaining);
  }

  // Excluded type can offer nothing; short-circuit before the profit-budget path.
  if (!isPlanTypeEligible(planType, limits)) {
    return 0;
  }

  let currentUnits: number;

  if (plans.length > 0 && unitPrice > 0) {
    const maxTotalSellAmount = calculateMaxTotalSellAmount({
      sellerParticipationLimits,
      plans,
      unitPrice,
      planType,
      retentionPercent,
    });

    currentUnits = calculateMaxUnitsForSellBudget({
      plans,
      maxTotalSellAmount,
      unitPrice,
      retentionPercent,
    });
  } else {
    currentUnits = calculateMaxUnitsSellerCanOfferLegacy({
      limits,
      available,
      unitPrice,
      planType,
    });
  }

  if (limits.maxUnits > 0) {
    currentUnits = Math.min(currentUnits, limits.maxUnits);
  }

  if (limits.maxPercentUnits > 0) {
    currentUnits = Math.min(
      currentUnits,
      Math.floor((available * limits.maxPercentUnits) / 100)
    );
  }

  return Math.min(Math.max(0, currentUnits), remaining);
};

export type SellerLimitsPlanBreakdownRow = {
  planId: string | null;
  beneficiaryId: string;
  planName: string;
  planType?: string;
  cappedUnits: number;
  sharePrice: number;
  maxPlanAmount: number;
};

export const calculateSellerLimitsPlanBreakdown = ({
  sellerParticipationLimits,
  plans = [],
  unitPrice = 0,
  planType,
  retentionPercent = 0,
}: Omit<SellerLimitsInput, 'totalAvailableUnits'>): SellerLimitsPlanBreakdownRow[] => {
  const limits = normalizeLimits(sellerParticipationLimits);

  if (limits.allowSellAll || !plans.length || unitPrice <= 0) {
    return [];
  }

  return plans
    .map((plan): SellerLimitsPlanBreakdownRow | null => {
      const cappedUnits = getPlanCappedUnits({ plan, limits, planType });

      if (cappedUnits <= 0) {
        return null;
      }

      const maxPlanAmount = getSellProfitTotal({
        plans: [{ ...plan, availableUnits: cappedUnits }],
        totalUnits: cappedUnits,
        unitPrice,
        retentionPercent,
      });

      return {
        planId: plan.planId,
        beneficiaryId: plan.beneficiaryId,
        planName: plan.planName,
        planType: plan.planType,
        cappedUnits,
        sharePrice: plan.sharePrice ?? 0,
        maxPlanAmount,
      };
    })
    .filter((row): row is SellerLimitsPlanBreakdownRow => row !== null);
};
